import { useEffect, useId, useState } from 'react';
import { cx } from '../cx';
import { Select } from './Select';

// 시각 선택 — 시(00~23)·분(00·30) Select 2개 (WP5 4단계 U0-4).
// 시·분을 둘 다 고를 때만 onChange('HH:mm'), 하나만 고르거나 지우면 onChange('').
// 기존 .field·.select 클래스만 사용(새 CSS·토큰 없음). contract 없는 손 작성 컴포넌트(SignalDot과 동일 예외).
export interface TimePickerProps {
  /** 'HH:mm'. 분이 00·30이 아니거나 형식이 틀리면 분을 미선택으로 표시(값을 보정하지 않음). */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** 'HH:mm' — 이 시각 이하의 선택지를 비활성화. */
  after?: string;
  error?: string;
  /** 입력 크기 — Select 의 size 와 동일(기본 md). 터치 영역 확보용 lg. */
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  id?: string;
  /** 필수 표시 — 라벨에 * 표시(스크린리더에는 숨김)와 두 Select 의 required. 선택 동작은 바뀌지 않음. */
  required?: boolean;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '30'];

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** value → [시, 분]. 형식이 틀리면 둘 다 ''. 분이 00·30이 아니면 분만 ''. */
function split(value: string): [string, string] {
  const m = TIME_RE.exec(value);
  if (!m) return ['', ''];
  return [m[1], MINUTES.includes(m[2]) ? m[2] : ''];
}

function toMinutes(hhmm: string | undefined): number | null {
  if (!hhmm) return null;
  const m = TIME_RE.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

export function TimePicker({ value, onChange, label, after, error, size = 'md', disabled, id, required, className = '' }: TimePickerProps) {
  const autoId = useId();
  const baseId = id ?? autoId;
  const [initH, initM] = split(value);
  const [hh, setHh] = useState(initH);
  const [mm, setMm] = useState(initM);

  // 외부 value 변경 반영. 부분 선택 중(value === '')에는 내부 값을 유지, 외부가 완성값을 비우면 같이 비움.
  useEffect(() => {
    if (value === '') {
      if (hh && mm) { setHh(''); setMm(''); }
      return;
    }
    const [h, m] = split(value);
    setHh(h);
    setMm(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (h: string, m: string) => onChange(h && m ? `${h}:${m}` : '');

  const afterMin = toMinutes(after);
  const hourDisabled = (h: string) => afterMin !== null && Number(h) * 60 + 30 <= afterMin;
  const minuteDisabled = (m: string) => afterMin !== null && hh !== '' && Number(hh) * 60 + Number(m) <= afterMin;

  return (
    <div className={cx('field', error && 'error', className)}>
      {label && (
        <label htmlFor={`${baseId}-h`}>
          {label}
          {required && <span className="req" aria-hidden="true">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        <Select
          id={`${baseId}-h`}
          aria-label="시"
          required={required}
          size={size}
          className="flex-1"
          value={hh}
          disabled={disabled}
          onChange={(e) => { setHh(e.target.value); emit(e.target.value, mm); }}
        >
          <option value="">시</option>
          {HOURS.map((h) => (
            <option key={h} value={h} disabled={hourDisabled(h)}>{h}</option>
          ))}
        </Select>
        <Select
          id={`${baseId}-m`}
          aria-label="분"
          required={required}
          size={size}
          className="flex-1"
          value={mm}
          disabled={disabled}
          onChange={(e) => { setMm(e.target.value); emit(hh, e.target.value); }}
        >
          <option value="">분</option>
          {MINUTES.map((m) => (
            <option key={m} value={m} disabled={minuteDisabled(m)}>{m}</option>
          ))}
        </Select>
      </div>
      {error && <span className="help">{error}</span>}
    </div>
  );
}
